import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Drawer, IconButton } from '@mui/material';
import {
  BarChart3,
  Archive,
  Carrot,
  ClipboardCheck,
  Globe2,
  Loader2,
  LogOut,
  Menu,
  MessageSquareText,
  ShieldCheck,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import NotificationCenter from '@/components/notifications/NotificationCenter';
import { MenuBarTour, type MenuBarTourStep } from '@/components/tutorial/MenuBarTour';
import { isDemoModeEnabled } from '@/demo/config';
import { useLogout } from '@/hooks/useLogout';
import {
  getCurrentUser,
  getNutritionistProfilePreferences,
  saveNutritionistProfilePreferences,
  USER_PROFILE_UPDATED_EVENT,
} from '@/lib/api/auth';
import {
  canAccessDashboardPath,
  getDefaultAuthenticatedRoute,
  hasAdminAccess,
  hasFullAppAccess,
  hasRecipeManagerAccess,
} from '@/lib/accessControl';
import { cn } from '@/lib/utils';
import logoCompact from '@/assets/images/logo-1.svg';
import logoFull from '@/assets/images/menu-bar-logo.svg';
import menuDashboard from '@/assets/images/menu-dashboard.svg';
import menuAppointments from '@/assets/images/menu-appointments.svg';
import menuMyClients from '@/assets/images/menu-my-clients.svg';
import menuMealPlanning from '@/assets/images/menu-meal-planning.svg';
import menuGenericMealPlans from '@/assets/images/menu-generic-meal-plans.svg';
import menuRecipesSmall from '@/assets/images/menu-recipes-small.svg';
import menuTemplates from '@/assets/images/menu-templates.svg';
import menuQuestionnaires from '@/assets/images/menu-questionnaires.svg';
import menuInvoiceBilling from '@/assets/images/menu-invoice-billing.svg';
import menuKnowledgeBase from '@/assets/images/menu-knowledge-base.svg';
import menuSettings from '@/assets/images/menu-settings.svg';
import menuHelp from '@/assets/images/menu-help.svg';

interface NavItem {
  title: string;
  url: string;
  icon: string | React.ComponentType<{ className?: string }>;
  tourId: string;
  tourDescription: string;
}

const MAIN_ITEMS: NavItem[] = [
  {
    title: 'Dashboard',
    url: '/dashboard',
    icon: menuDashboard,
    tourId: 'dashboard',
    tourDescription: 'Start here for today’s schedule, client activity, revenue, and practice alerts.',
  },
  {
    title: 'My Clients',
    url: '/clients',
    icon: menuMyClients,
    tourId: 'my-clients',
    tourDescription: 'Open your client list to add clients, review profiles, and continue their care workflow.',
  },
  {
    title: 'Appointments',
    url: '/appointments',
    icon: menuAppointments,
    tourId: 'appointments',
    tourDescription: 'Use appointments to review sessions, manage bookings, and keep your calendar current.',
  },
  {
    title: 'Meal Planning',
    url: '/meal-planning',
    icon: menuMealPlanning,
    tourId: 'meal-planning',
    tourDescription: 'Build, review, and finalize personalized meal plans from this section.',
  },
  {
    title: 'Cohort Meal Plans',
    url: '/generic-meal-plans',
    icon: menuGenericMealPlans,
    tourId: 'generic-meal-plans',
    tourDescription: 'Run structured nutrition programs while maintaining personalized nutrition care.',
  },
  {
    title: 'Ingredients',
    url: '/ingredients',
    icon: Carrot,
    tourId: 'ingredients',
    tourDescription: 'Manage ingredients and nutrition details used across recipes and meal plans.',
  },
  {
    title: 'Recipes',
    url: '/recipes',
    icon: menuRecipesSmall,
    tourId: 'recipes',
    tourDescription: 'Create, review, and organize recipes for your nutrition planning library.',
  },
  {
    title: 'Templates',
    url: '/templates',
    icon: menuTemplates,
    tourId: 'templates',
    tourDescription: 'Use templates to reuse common plans and speed up repeat client workflows.',
  },
  {
    title: 'Questionnaires',
    url: '/questionnaires',
    icon: menuQuestionnaires,
    tourId: 'questionnaires',
    tourDescription: 'Send and review questionnaires so intake details stay structured.',
  },
  {
    title: 'Invoice and Billing',
    url: '/invoicing',
    icon: menuInvoiceBilling,
    tourId: 'invoice-billing',
    tourDescription: 'Track billing, invoices, and payments here.',
  },
  {
    title: 'Knowledge Base',
    url: '/knowledgebase',
    icon: menuKnowledgeBase,
    tourId: 'knowledge-base',
    tourDescription: 'Find support content, product guidance, and useful nutrition resources.',
  },
];

const SETTINGS_ITEMS: NavItem[] = [
  {
    title: 'Role Management',
    url: '/admin/users',
    icon: ShieldCheck,
    tourId: 'role-management',
    tourDescription: 'Manage users and access permissions for the practice.',
  },
  {
    title: 'Usage & Quality',
    url: '/admin/usage-quality',
    icon: BarChart3,
    tourId: 'usage-quality',
    tourDescription: 'Track weekly usage, generated meal plans, and AI cohort membership.',
  },
  {
    title: 'Landing Tracking',
    url: '/admin/landing-operations',
    icon: Globe2,
    tourId: 'landing-tracking',
    tourDescription: 'Review first-party landing page location and server-side tracking aggregates.',
  },
  {
    title: 'Recipe Review',
    url: '/admin/custom-recipes',
    icon: ClipboardCheck,
    tourId: 'recipe-review',
    tourDescription: 'Review submitted recipes before they become available in the library.',
  },
  {
    title: 'AI Recipe Review',
    url: '/admin/ai-recipes',
    icon: Archive,
    tourId: 'ai-recipe-review',
    tourDescription: 'Inspect and manage the daily archive of AI-generated meal-plan recipes.',
  },
  {
    title: 'Feedback Review',
    url: '/admin/feedback',
    icon: MessageSquareText,
    tourId: 'feedback-review',
    tourDescription: 'Review product and recipe feedback from users.',
  },
  {
    title: 'Settings',
    url: '/profile?view=hub',
    icon: menuSettings,
    tourId: 'settings',
    tourDescription: 'Update profile, availability, practice information, and account preferences.',
  },
  {
    title: 'Help',
    url: '/knowledgebase',
    icon: menuHelp,
    tourId: 'help',
    tourDescription: 'Use Help when you need product guidance or support details.',
  },
];

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'dashboard_sidebar_collapsed';
const SETTINGS_HUB_PATH = '/profile?view=hub';
const SIDEBAR_BACKGROUND_GRADIENT = 'linear-gradient(115.13deg, #EFFDFF 2.62%, #CFE5E7 41.03%, #CBF7FF 95.57%)';
const COLLAPSED_ACTIVE_ICON_FILTER = 'brightness(0) saturate(100%) invert(59%) sepia(85%) saturate(1808%) hue-rotate(162deg) brightness(90%) contrast(89%)';
type MenuTourSurface = 'desktop' | 'mobile';

const SidebarToggleIcon: React.FC<{ collapsed: boolean; className?: string }> = ({ collapsed, className }) => (
  collapsed ? (
    <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M15.8333 2.5H4.16667C3.24619 2.5 2.5 3.24619 2.5 4.16667V15.8333C2.5 16.7538 3.24619 17.5 4.16667 17.5H15.8333C16.7538 17.5 17.5 16.7538 17.5 15.8333V4.16667C17.5 3.24619 16.7538 2.5 15.8333 2.5Z" stroke="#20AEDC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7.5 2.5V17.5" stroke="#20AEDC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11.6667 7.5L14.1667 10L11.6667 12.5" stroke="#20AEDC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M15.8333 2.5H4.16667C3.24619 2.5 2.5 3.24619 2.5 4.16667V15.8333C2.5 16.7538 3.24619 17.5 4.16667 17.5H15.8333C16.7538 17.5 17.5 16.7538 17.5 15.8333V4.16667C17.5 3.24619 16.7538 2.5 15.8333 2.5Z" stroke="#20AEDC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7.5 2.5V17.5" stroke="#20AEDC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.3333 12.5L10.8333 10L13.3333 7.5" stroke="#20AEDC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
);

const DashboardSidebar: React.FC = () => {
  const location = useLocation();
  const { handleLogout, isLoggingOut } = useLogout();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(false);
  const [isDesktopHoverExpanded, setIsDesktopHoverExpanded] = useState(false);
  const [shouldShowMenuTour, setShouldShowMenuTour] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => getCurrentUser());
  const isDemoMode = isDemoModeEnabled();
  const defaultRoute = getDefaultAuthenticatedRoute(currentUser);
  const canUseProfilePreferences = hasFullAppAccess(currentUser);
  const visibleMainItems = useMemo(
    () => (isDemoMode ? MAIN_ITEMS : MAIN_ITEMS.filter((item) => canAccessDashboardPath(item.url, currentUser))),
    [currentUser, isDemoMode]
  );
  const visibleSettingsItems = useMemo(
    () => {
      if (isDemoMode) {
        return SETTINGS_ITEMS.filter((item) => !item.url.startsWith('/admin'));
      }

      if (!hasFullAppAccess(currentUser) && !hasAdminAccess(currentUser) && !hasRecipeManagerAccess(currentUser)) {
        return [];
      }

      return SETTINGS_ITEMS.filter((item) => {
        if (item.url.startsWith('/admin')) {
          if (item.url === '/admin/ai-recipes') {
            return hasRecipeManagerAccess(currentUser);
          }
          return hasAdminAccess(currentUser);
        }
        return canAccessDashboardPath(item.url, currentUser);
      });
    },
    [currentUser, isDemoMode]
  );
  const visibleNavItems = useMemo(
    () => [...visibleMainItems, ...visibleSettingsItems],
    [visibleMainItems, visibleSettingsItems]
  );
  const canOpenSettingsHub = isDemoMode || canAccessDashboardPath(SETTINGS_HUB_PATH, currentUser);
  const menuTourSteps = useMemo<MenuBarTourStep[]>(
    () => visibleNavItems.map((item) => ({
      id: item.tourId,
      title: item.title,
      description: item.tourDescription,
    })),
    [visibleNavItems]
  );

  const userName =
    currentUser?.fullName?.trim()
    || currentUser?.email?.split('@')?.[0]
    || 'Nutritionist';
  const userRole = hasAdminAccess(currentUser)
    ? 'Admin'
    : hasFullAppAccess(currentUser)
      ? 'Nutritionist'
      : 'Recipe Manager';

  const initials = useMemo(() => {
    const parts = userName.split(' ').filter(Boolean);
    if (parts.length === 0) return 'NB';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
  }, [userName]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsDesktopHoverExpanded(false);
  }, [location.pathname]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY);
      if (stored === '1') {
        setIsDesktopCollapsed(true);
      }
    } catch {
      // Ignore storage read failures.
    }
  }, []);

  useEffect(() => {
    const syncCurrentUser = () => {
      setCurrentUser(getCurrentUser());
    };

    window.addEventListener(USER_PROFILE_UPDATED_EVENT, syncCurrentUser);
    window.addEventListener('storage', syncCurrentUser);

    return () => {
      window.removeEventListener(USER_PROFILE_UPDATED_EVENT, syncCurrentUser);
      window.removeEventListener('storage', syncCurrentUser);
    };
  }, []);

  useEffect(() => {
    let isCancelled = false;

    if (isDemoMode || !canUseProfilePreferences) {
      setShouldShowMenuTour(false);
      return () => {
        isCancelled = true;
      };
    }

    setShouldShowMenuTour(false);
    void getNutritionistProfilePreferences()
      .then((preferences) => {
        if (!isCancelled) {
          setShouldShowMenuTour(preferences.menuTutorial !== true);
        }
      })
      .catch((error) => {
        console.error('Failed to load menu tour preference:', error);
      });

    return () => {
      isCancelled = true;
    };
  }, [canUseProfilePreferences, currentUser?.id, isDemoMode]);

  const handleMenuTourComplete = useCallback(() => {
    setShouldShowMenuTour(false);
    void saveNutritionistProfilePreferences({ menuTutorial: true }).catch((error) => {
      console.error('Failed to save menu tour completion:', error);
    });
  }, []);

  const setDesktopCollapsed = (nextCollapsed: boolean) => {
    setIsDesktopCollapsed(nextCollapsed);
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, nextCollapsed ? '1' : '0');
    } catch {
      // Ignore storage write failures.
    }
  };

  const isActivePath = (path: string) => {
    const matches = location.pathname === path || location.pathname.startsWith(`${path}/`);
    if (!matches) return false;
    // Only active if this is the most specific (longest) matching path, so parent and child don't both highlight
    const longestMatchingPath = visibleNavItems
      .filter((item) => location.pathname === item.url || location.pathname.startsWith(`${item.url}/`))
      .sort((a, b) => b.url.length - a.url.length)[0]?.url;
    return longestMatchingPath === path;
  };

  const desktopShowsCollapsedRail = isDesktopCollapsed && !isDesktopHoverExpanded;

  const handleDesktopNavigate = () => {
    // Match mobile behavior: selecting a nav item closes the sidebar on desktop too.
    setDesktopCollapsed(true);
    setIsDesktopHoverExpanded(false);
  };

  const renderNavList = (
    items: NavItem[],
    isCollapsed: boolean,
    tourSurface: MenuTourSurface,
    onNavigate?: () => void
  ) => (
    <ul className="space-y-2">
      {items.map((item) => {
        const active = isActivePath(item.url);
        return (
          <li key={item.title}>
            <Link
              to={item.url}
              onClick={onNavigate}
              title={isCollapsed ? item.title : undefined}
              aria-label={item.title}
              data-menu-tour-id={item.tourId}
              data-menu-tour-surface={tourSurface}
              className={cn(
                // Figma: 14px Medium, 20px line-height, tracking -0.28px; row py-[10px] px-[12px], gap 12px
                'flex min-h-10 items-center gap-3 py-2.5 text-sm font-medium leading-5 tracking-[-0.28px] transition-colors duration-150',
                isCollapsed
                  ? 'mx-auto size-11 justify-center rounded-2xl p-0'
                  : 'w-full rounded-lg px-3',
                !isCollapsed && active && 'rounded-full bg-white text-[#20AEDC] shadow-[0_1px_2px_rgba(15,23,42,0.06)]',
                !isCollapsed && !active && 'text-black/80 hover:bg-white/60 hover:text-[#20AEDC]',
                isCollapsed && active && 'bg-white text-[#20AEDC] shadow-[0_1px_2px_rgba(15,23,42,0.06)]',
                isCollapsed && !active && 'text-[#1f2b34] hover:bg-white/60 hover:text-[#20AEDC]'
              )}
            >
              {typeof item.icon === 'string' ? (
                <img
                  src={item.icon}
                  alt=""
                  aria-hidden="true"
                  className={cn('h-5 w-5 shrink-0 object-contain', active ? 'opacity-100' : 'opacity-90')}
                  style={active ? { filter: COLLAPSED_ACTIVE_ICON_FILTER } : undefined}
                />
              ) : (
                <item.icon
                  className={cn('h-5 w-5 shrink-0', active ? 'text-[#20AEDC]' : 'text-[#1f2b34] opacity-90')}
                  aria-hidden="true"
                />
              )}
              {!isCollapsed ? <span className="min-w-0 flex-1 truncate">{item.title}</span> : null}
            </Link>
          </li>
        );
      })}
    </ul>
  );

  const renderSidebarContent = (
    isCollapsed: boolean,
    onNavigate?: () => void,
    showCollapseToggle = !onNavigate,
    tourSurface: MenuTourSurface = 'desktop'
  ) => (
    <>
      <div className={cn('px-5 pb-6 pt-6 sm:px-6 lg:py-7', isCollapsed ? 'lg:px-3' : 'lg:px-7')}>
        <div className={cn('relative flex items-center', isCollapsed ? 'justify-center' : 'justify-between')}>
          <Link to={defaultRoute} className="flex items-center justify-center" onClick={onNavigate}>
            {isCollapsed ? (
              <img src={logoCompact} alt="CalorieScience Logo" className="h-8 w-auto" />
            ) : (
              <img src={logoFull} alt="CalorieScience Logo" className="h-9 w-auto max-w-full lg:h-10" />
            )}
          </Link>
        </div>
      </div>

      <div
        className={cn(
          'flex-1 overflow-y-auto pb-5',
          isCollapsed ? 'px-2' : 'px-4 [scrollbar-gutter:stable]'
        )}
      >
      
        <div>
          {!isCollapsed ? (
            <p
              className="mb-2 px-3 text-xs font-medium uppercase leading-[12px] tracking-[0.4px] text-[#757575]"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              Main
            </p>
          ) : null}
          {renderNavList(visibleMainItems, isCollapsed, tourSurface, onNavigate)}
        </div>

        {visibleSettingsItems.length > 0 ? (
        <div className={cn(isCollapsed ? 'mt-4' : 'mt-[41px]')}>
          {!isCollapsed ? (
            <p
              className="mb-2 px-3 text-xs font-medium uppercase leading-[12px] tracking-[0.4px] text-[#757575]"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              Settings
            </p>
          ) : null}
          {renderNavList(visibleSettingsItems, isCollapsed, tourSurface, onNavigate)}
        </div>
        ) : null}
      </div>

      <div className={cn('flex flex-col gap-3.5 py-5 sm:py-6', isCollapsed ? 'px-2' : 'px-4')}>
        {isCollapsed ? (
          <div className="flex flex-col items-center gap-3">
            <NotificationCenter
              align="start"
              buttonClassName="h-10 w-10 border-[#c7d5de]"
            />
            {canOpenSettingsHub ? (
              <Link
                to={SETTINGS_HUB_PATH}
                onClick={onNavigate}
                title="Settings"
                aria-label="Open settings"
                className="rounded-full transition-opacity hover:opacity-80"
              >
                <Avatar className="h-10 w-10 border border-[#c7d5de]">
                  <AvatarImage src={currentUser?.profileImageUrl || ''} alt={userName} className="object-cover object-center" />
                  <AvatarFallback className="bg-cs-primary/15 text-cs-primary">{initials}</AvatarFallback>
                </Avatar>
              </Link>
            ) : (
              <Avatar className="h-10 w-10 border border-[#c7d5de]">
                <AvatarImage src={currentUser?.profileImageUrl || ''} alt={userName} className="object-cover object-center" />
                <AvatarFallback className="bg-cs-primary/15 text-cs-primary">{initials}</AvatarFallback>
              </Avatar>
            )}
            <button
              type="button"
              title={isLoggingOut ? 'Logging out...' : 'Logout'}
              aria-label={isLoggingOut ? 'Logging out...' : 'Logout'}
              onClick={() => {
                onNavigate?.();
                void handleLogout();
              }}
              disabled={isLoggingOut}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[#d55f5a] transition-colors hover:bg-[#feecec] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLoggingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
            </button>
          </div>
        ) : (
          <>
            <div className="flex h-[42px] items-center gap-4 px-3">
              {canOpenSettingsHub ? (
                <Link
                  to={SETTINGS_HUB_PATH}
                  onClick={onNavigate}
                  aria-label="Open settings"
                  className="flex min-w-0 flex-1 items-center gap-4 rounded-lg transition-colors hover:bg-white/60"
                >
                  <Avatar className="h-10 w-10 shrink-0 border border-[#c7d5de]">
                    <AvatarImage src={currentUser?.profileImageUrl || ''} alt={userName} className="object-cover object-center" />
                    <AvatarFallback className="bg-cs-primary/15 text-cs-primary">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <p
                      className="truncate text-base font-medium leading-[22px] tracking-[-0.43px] text-[#242424]"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                    >
                      {userName}
                    </p>
                    <p
                      className="truncate text-sm font-normal leading-5 tracking-[-0.23px] text-[#616161]"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                    >
                      {userRole}
                    </p>
                  </div>
                </Link>
              ) : (
                <>
                  <Avatar className="h-10 w-10 shrink-0 border border-[#c7d5de]">
                    <AvatarImage src={currentUser?.profileImageUrl || ''} alt={userName} className="object-cover object-center" />
                    <AvatarFallback className="bg-cs-primary/15 text-cs-primary">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <p
                      className="truncate text-base font-medium leading-[22px] tracking-[-0.43px] text-[#242424]"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                    >
                      {userName}
                    </p>
                    <p
                      className="truncate text-sm font-normal leading-5 tracking-[-0.23px] text-[#616161]"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                    >
                      {userRole}
                    </p>
                  </div>
                </>
              )}
              <NotificationCenter
                align="start"
                buttonClassName="h-9 w-9 shrink-0 border-[#c7d5de]"
              />
            </div>

            <button
              type="button"
              onClick={() => {
                onNavigate?.();
                void handleLogout();
              }}
              disabled={isLoggingOut}
              className="flex min-h-10 w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium leading-5 tracking-[-0.28px] text-[#d55f5a] transition-colors hover:bg-white/60 disabled:cursor-not-allowed disabled:opacity-60"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              {isLoggingOut ? <Loader2 className="h-5 w-5 shrink-0 animate-spin" /> : <LogOut className="h-5 w-5 shrink-0" />}
              <span className="min-w-0 flex-1 text-left">{isLoggingOut ? 'Logging out...' : 'Logout Account'}</span>
            </button>
          </>
        )}
      </div>
    </>
  );

  return (
    <>
      <div
        className="sticky top-0 z-30 border-b border-[#c7d5de]/80 px-3 py-3 sm:px-4 md:px-5 lg:hidden"
        style={{ background: SIDEBAR_BACKGROUND_GRADIENT }}
      >
        <div className="flex items-center justify-between gap-3">
          <IconButton
            aria-label="Open navigation menu"
            onClick={() => setIsMobileMenuOpen(true)}
            onMouseEnter={() => setIsMobileMenuOpen(true)}
            className="!inline-flex !h-11 !w-11 !shrink-0 !rounded-full"
            sx={{
              border: '1px solid #b9cad5',
              backgroundColor: '#ffffff',
              color: '#1f2b34',
              transition: 'background-color 150ms ease',
              '&:hover': { backgroundColor: '#f5fafc' },
            }}
          >
            <Menu className="h-5 w-5" />
          </IconButton>

          <Drawer
            anchor="left"
            open={isMobileMenuOpen}
            onClose={() => setIsMobileMenuOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              '& .MuiDrawer-paper': {
                width: 'min(88vw, 22rem)',
                maxWidth: 'none',
                borderRight: '1px solid #c7d5de',
                background: SIDEBAR_BACKGROUND_GRADIENT,
                color: '#1f2b34',
              },
            }}
          >
            <h2 className="sr-only">Navigation Menu</h2>
            {renderSidebarContent(false, () => setIsMobileMenuOpen(false), false, 'mobile')}
          </Drawer>

          <Link to={defaultRoute} className="flex min-w-0 flex-1 items-center justify-center">
            <img src={logoFull} alt="CalorieScience Logo" className="h-8 w-auto max-w-[11rem] sm:h-9" />
          </Link>

          <NotificationCenter buttonClassName="h-11 w-11 shrink-0" />
        </div>
      </div>

      <aside
        data-demo-id="dashboard-sidebar"
        className={cn(
          'hidden shrink-0 lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col lg:overflow-hidden lg:transition-[width] lg:duration-200',
          desktopShowsCollapsedRail ? 'lg:w-[88px]' : 'lg:w-[260px]'
        )}
        onMouseEnter={() => {
          if (isDesktopCollapsed) {
            setIsDesktopHoverExpanded(true);
          }
        }}
        onMouseLeave={() => {
          if (isDesktopCollapsed) {
            setIsDesktopHoverExpanded(false);
          }
        }}
      >
        {renderSidebarContent(desktopShowsCollapsedRail, handleDesktopNavigate, true, 'desktop')}
      </aside>

      {shouldShowMenuTour && menuTourSteps.length > 0 ? (
        <MenuBarTour steps={menuTourSteps} onComplete={handleMenuTourComplete} />
      ) : null}
    </>
  );
};

export default DashboardSidebar;
